"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useMemo, useState } from "react";
import { cx } from "../internal/cx.js";
import { IconArrowRight, IconChevronRight, IconClock, IconFileCode, IconMapPin, IconSearch, IconSparkles } from "../internal/icons.js";
import { newestFirst, noteGuide, placeHref, placesOf, searchWhere } from "../whatsNew.js";
import { Badge } from "./Badge.js";
import { Banner } from "./Banner.js";
import { ButtonLink } from "./Button.js";
import { Chip } from "./Chip.js";
import { EmptyState } from "./EmptyState.js";
import { Panel } from "./Panel.js";
import { Spinner } from "./Spinner.js";
import { TextField } from "./TextField.js";
export const WHATS_NEW_TEXTS = {
    title: "Что нового",
    whereTitle: "Где это?",
    searchLabel: "Что найти",
    searchPlaceholder: "Например: архив",
    notFound: "Не нашлось — другие слова?",
    empty: "Заметок пока нет",
    loading: "Загрузка",
    open: "Открыть",
    openPlace: "Открыть это место",
    show: (label) => `Показать: ${label}`,
};
/** «[words](/address)» → a link, the rest — text. */
function inline(text, follow, guideOf) {
    return text.split(/(\[[^\]\n]+\]\([^)\s]+\))/g).map((part, index) => {
        const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
        if (!link)
            return _jsx(Fragment, { children: part }, index);
        const href = link[2];
        return _jsx("a", { href: href, onClick: (event) => follow(event, href, guideOf({ label: link[1], href })), children: link[1] }, index);
    });
}
/** Paragraphs, «## » — a subheading, «- » — a list item. */
function NoteBody({ body, follow, guideOf }) {
    const blocks = [];
    let items = [];
    const flush = () => {
        if (items.length)
            blocks.push(_jsx("ul", { children: items.map((item, index) => _jsx("li", { children: inline(item, follow, guideOf) }, index)) }, blocks.length));
        items = [];
    };
    for (const line of body.split("\n")) {
        if (line.startsWith("- ")) {
            items.push(line.slice(2));
            continue;
        }
        flush();
        if (line.startsWith("## "))
            blocks.push(_jsx("h4", { children: line.slice(3) }, blocks.length));
        else if (line.trim())
            blocks.push(_jsx("p", { children: inline(line, follow, guideOf) }, blocks.length));
    }
    flush();
    return _jsx("div", { className: "jui-wn-body", children: blocks });
}
/** «Настройки → Задачи» with its address: a click opens exactly that place. */
function Place({ place, title, follow, tip }) {
    const steps = place.label.split(" → ");
    const content = _jsxs(_Fragment, { children: [_jsx(IconMapPin, { small: true }), steps.map((step, index) => _jsxs(Fragment, { children: [index > 0 && _jsx(IconChevronRight, { small: true }), _jsx("span", { children: step })] }, index))] });
    if (!place.href)
        return _jsx("span", { className: "jui-wn-place jui-wn-place--off", children: content });
    const href = place.href;
    return _jsx("a", { className: "jui-wn-place", href: href, title: tip, onClick: (event) => follow(event, href, { href, place: place.place, label: place.label, title, hint: place.hint }), children: content });
}
const shownDate = (note) => `${note.date.split("-").reverse().join(".")}${note.time ? ` · ${note.time}` : ""}`;
/**
 * «Что нового»: «Где это?» over the host's index, then every release note, newest first. A click on a note (its title or
 * «Открыть») opens the place of the change; links in its text and places in the answer open theirs. Data and navigation
 * come from the host; the screen keeps only the search query.
 */
export function WhatsNew({ notes, index, error, examples = [], navigate, onPlace, onNavigated, texts, className }) {
    const t = { ...WHATS_NEW_TEXTS, ...texts };
    const [query, setQuery] = useState("");
    const list = useMemo(() => newestFirst(notes ?? []), [notes]);
    const where = index ?? [];
    const found = useMemo(() => (query.trim() ? searchWhere(where, query) : []), [where, query]);
    // A plain left click on an app address goes through the host's router; anything else is the browser's.
    const follow = (event, href, guide) => {
        if (!href.startsWith("/") || href.startsWith("//"))
            return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
            return;
        if (guide)
            onPlace?.(guide);
        if (!navigate)
            return;
        event.preventDefault();
        try {
            navigate(href);
        }
        catch {
            window.location.assign(href);
            return;
        }
        onNavigated?.(guide, href);
    };
    return _jsxs("div", { className: cx("jui-wn", className), children: [where.length > 0 && _jsxs(Panel, { title: t.whereTitle, icon: _jsx(IconSearch, {}), children: [_jsx(TextField, { type: "search", label: t.searchLabel, hideLabel: true, icon: _jsx(IconSearch, {}), placeholder: t.searchPlaceholder, value: query, onChange: (event) => setQuery(event.target.value) }), !query && examples.length > 0 && _jsx("div", { className: "jui-wn-chips", children: examples.map((example) => _jsx(Chip, { variant: "outline", onClick: () => setQuery(example), children: example }, example)) }), query.trim() && found.length === 0 && _jsx(EmptyState, { compact: true, icon: _jsx(IconSearch, {}), children: t.notFound }), found.length > 0 && _jsx("ul", { className: "jui-wn-found", "aria-label": t.whereTitle, children: found.map((entry) => _jsxs("li", { children: [_jsx("b", { children: entry.title }), _jsx("div", { className: "jui-wn-places", children: entry.places.map((place) => _jsx(Place, { place: place, title: entry.title, follow: follow, tip: t.openPlace }, place.label)) }), !!entry.files?.length && _jsx("div", { className: "jui-wn-files", children: entry.files.map((file) => _jsxs("span", { children: [_jsx(IconFileCode, { small: true }), " ", file] }, file)) })] }, `${entry.kind ?? ""}:${entry.title}`)) })] }), _jsxs(Panel, { title: t.title, icon: _jsx(IconSparkles, {}), count: notes ? list.length : undefined, actions: !notes && !error ? _jsx(Spinner, { size: "sm", label: t.loading }) : undefined, children: [error && _jsx(Banner, { tone: "error", children: error }), notes && list.length === 0 && !error && _jsx(EmptyState, { icon: _jsx(IconSparkles, {}), children: t.empty }), list.length > 0 && _jsx("ol", { className: "jui-wn-notes", children: list.map((note) => {
                            const href = placeHref(note);
                            const place = placesOf(note).find((link) => link.href === href);
                            const guide = place ? noteGuide(note, place) : null;
                            const guideOf = (link) => noteGuide(note, link);
                            return _jsxs("li", { className: "jui-wn-note", children: [_jsxs("div", { className: "jui-wn-head", children: [_jsx(Badge, { icon: _jsx(IconClock, { small: true }), className: "jui-wn-date", children: shownDate(note) }), href
                                                ? _jsx("a", { className: "jui-wn-title", href: href, title: place ? t.show(place.label) : t.openPlace, onClick: (event) => follow(event, href, guide), children: note.title })
                                                : _jsx("strong", { className: "jui-wn-title", children: note.title }), href && _jsx(ButtonLink, { className: "jui-wn-open", variant: "quiet", size: "sm", iconEnd: _jsx(IconArrowRight, { small: true }), href: href, onClick: (event) => follow(event, href, guide), children: t.open })] }), _jsx(NoteBody, { body: note.body ?? "", follow: follow, guideOf: guideOf })] }, `${note.date}-${note.time ?? ""}-${note.title}`);
                        }) })] })] });
}
